import React, { useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Context } from '@front10/helpers/dist/Context';
import { modifiers, isPastDate } from '../../../helpers/dayCard/dayCard';

import Box from '../../elements/Box';
import Text from '../../elements/Text';
import Button from '../../elements/Button';
import FareItemReturnInfo from '../_commons/atoms/FareItemReturnInfo';
import FareItemPrice from '../_commons/atoms/FareItemPrice';
import FareItemMiles from '../_commons/atoms/FareItemMiles';
import FareItemTravelClass from '../_commons/atoms/FareItemTravelClass';
import FareItemLastSeen from '../_commons/atoms/FareItemLastSeen';
import FareItemNoPriceAvailable from '../_commons/atoms/FareItemNoPriceAvailableCalendar';
import FareItemCallToAction from '../_commons/atoms/FareItemCallToAction';
import FareItemJourneyType from '../_commons/atoms/FareItemJourneyType';
import FareItemDate from '../_commons/atoms/FareItemDate';
import Separator from '../_commons/atoms/Separator';
import ErrorBoundary from '../../utils/ErrorBoundary';
import { getDateAriaLabel } from '../_commons/atomsHelpers';
import { handleFareItemInteraction } from '../_commons/commonFns';
import { weekDaysLong } from './../../../helpers/localization/localizationUtils';

const KEY_ENTER = 13;
const KEY_SPACE = 32;
const DayCard = (props) => {
  const {
    data,
    day,
    showReturnInfo,
    showTravelClass,
    showJourneyType,
    showLastSeen,
    showPrice,
    showMiles,
    showNoPriceAvailableCallToAction,
    showCallToAction,
    loading,
    isLowest,
    usedErrorBoundary,
    tabIndex,
    tabIndexCallToAction,
    showDate,
    showSeparator,
    effectScale,
    focusReferences,
    isToday,
    ariaLabel,
    ariaLabelFormat,
    isDisregarded,
    isOutside,
    disregardedBehavior,
    dayDateFormat,
    showJourneyTypeTravelClassBeforePrice,
    handlerClick,
    enabledCallToAction = true,
    showAsEmptyDay = false,
    isBlackoutDay,
    setFocusDate,
    focusDate,
    accessibleByArrowKeys,
    selectedDate,
    isFirstDateFocusable
  } = props;
  const { translate, localizationData } = useContext(Context);
  const daysLabels = weekDaysLong(translate, localizationData);
  const getDayInformation = () => {
    const dayName = day ? daysLabels[day?.getDay()] : '';
    const prefixArialLabel = translate('cmp-low-fare-calendar-oi-date-available', `Choose`);
    return `${prefixArialLabel} ${dayName}`;
  };
  let srMessage = '' + getDayInformation();
  const onBlurAction = useRef(false);

  const dayCard = modifiers(props);

  let containeClassName = 'fr-day-card';
  if (effectScale) {
    containeClassName += ' fr-effects fr-effect-scale';
  }
  const loadingStyle = loading
    ? {
        backgroundColor: 'lightgray',
        animation: 'pulse 1s infinite ease-in-out'
      }
    : {};

  const {
    handleCallToAction,
    handleClick,
    handleCallToActionKeyDown,
    hasInteraction
  } = handleFareItemInteraction(props);

  const cursorClass = hasInteraction ? 'fr-cursor-pointer' : '';

  if (ariaLabel && data && data.dataType && data.dataType === 'DATA_API') {
    srMessage += `. ${ariaLabel}`;
  }
  if (isLowest) {
    srMessage += `${srMessage ? ',' : ''} ${translate(
      'cmp-legend-lowest-price',
      'Lowest price'
    )}`.trim();
  }
  if (isToday) {
    srMessage += `${srMessage ? ',' : ''} ${translate(
      'cmp-legend-current-day',
      'Current day'
    )}`.trim();
  }
  srMessage = srMessage || null;

  let ariaLabelDay = null;
  if (data && data.departureDate) {
    ariaLabelDay = getDateAriaLabel(
      data.departureDate,
      translate,
      ariaLabelFormat,
      localizationData
    );
  } else if (day) {
    ariaLabelDay = getDateAriaLabel(day, translate, ariaLabelFormat, localizationData);
  }
  let dataTestId = 'dayCard';
  if (isOutside) {
    dataTestId = 'dayCard--outside';
  }
  if (isDisregarded) {
    dataTestId = 'dayCard--disregarded';
  }
  if (isPastDate(day)) {
    dataTestId = 'dayCard--pastdate';
  }
  const isSameDay = dayjs(day).isSame(focusDate, 'day');
  let isInteractive = accessibleByArrowKeys
    ? isSameDay && !props.isEmptyCard
    : !(isPastDate(day) || isDisregarded || !tabIndex || isOutside);
  isInteractive = props.isEmptyCard ? false : isInteractive;

  const journeyTypeTravelClassRender = (
    <Box display="flex" rtlReverse {...dayCard.dayCardJourneyTypeTravelClass}>
      {showTravelClass && <FareItemTravelClass {...data} {...dayCard.dayCardTravelClass} />}
      {showTravelClass && showJourneyType && <Separator {...dayCard.dayCardSeparatorJourneyType} />}
      {showJourneyType && <FareItemJourneyType {...data} {...dayCard.dayCardJourneyType} />}
    </Box>
  );

  const dayCardRef = useRef(null);
  useEffect(() => {
    if (accessibleByArrowKeys && focusDate && selectedDate) {
      if (
        dayjs(focusDate).isSame(day, 'day') &&
        dayjs(focusDate).isSame(selectedDate, 'day') &&
        props.makeClickAction !== undefined &&
        !props.makeClickAction
      ) {
        dayCardRef?.current.focus();
      } else if (
        dayjs(focusDate).isSame(day, 'day') &&
        props.makeClickAction !== undefined &&
        !props.makeClickAction
      ) {
        dayCardRef?.current.focus();
      } else if (
        !dayjs(selectedDate).isSame(focusDate, 'day') &&
        dayjs(selectedDate).isSame(day, 'day') &&
        props.makeClickAction !== undefined &&
        !props.makeClickAction
      ) {
        dayCardRef?.current.focus();
      }
      if (props.updateMakeClickAction && !props.makeClickAction && !onBlurAction.current) {
        props.updateMakeClickAction(false);
      } else {
        onBlurAction.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDate]);

  const cardRender = (
    <Box
      display="flex"
      key={selectedDate}
      flexDirection="column"
      role={isInteractive ? 'button' : undefined}
      dataTestId={dataTestId}
      {...dayCard}
      ariaLabel={null}
      focusReferences={accessibleByArrowKeys ? dayCardRef : focusReferences}
      onClick={(e) => {
        return enabledCallToAction ? handleClick() : handlerClick(day, e);
      }}
      tabIndex={isInteractive ? 0 : null}
      focusable={tabIndex}
      className={`${containeClassName} ${cursorClass}`}
      style={Object.assign({}, dayCard.style, loadingStyle)}
      onKeyDown={(e) => {
        if (e.keyCode === KEY_ENTER || e.keyCode === KEY_SPACE) {
          enabledCallToAction ? handleClick() : handlerClick(day, e);
        } else if (setFocusDate) {
          setFocusDate(day, e);
        }
      }}
      onBlur={() => {
        if (props.updateFocusDate) {
          onBlurAction.current = true;
          props.updateFocusDate();
        }
      }}
    >
      {srMessage && (
        <Text className="fr-sr-only">
          {data && data.dataType && data.dataType !== 'DATA_API'
            ? `${srMessage} ${
                isFirstDateFocusable
                  ? translate(
                      'cmp-low-fare-calendar-oi-container-explain',
                      'This is a calendar with the cheapest prices per day. Press the LEFT and RIGHT arrow keys to navigate the row by weekday. Press the UP and DOWN arrow keys to navigate between weeks on the same weekday, Press the ENTER key to activate the selected date'
                    )
                  : ''
              }`
            : srMessage}
        </Text>
      )}

      {isBlackoutDay ? (
        <>
          {!loading && dayCard.showDay && ariaLabelDay && (
            <>
              <Text
                position="absolute"
                right="1"
                top="1"
                {...dayCard.dayCardDay}
                ariaLabel={`${ariaLabelDay} ${translate(
                  'cmp-day-card-blackout-date',
                  'Blackout Date'
                )}`}
              >
                {dayjs(day).format(dayDateFormat)}
              </Text>
            </>
          )}
        </>
      ) : showAsEmptyDay ? (
        <>
          {!loading && dayCard.showDay && ariaLabelDay && (
            <>
              <Text
                position="absolute"
                right="1"
                top="1"
                {...dayCard.dayCardDay}
                ariaLabel={ariaLabelDay}
              >
                {dayjs(day).format(dayDateFormat)}
              </Text>
            </>
          )}
        </>
      ) : (
        !isOutside && (
          <>
            {!showDate && !dayCard.showDay && ariaLabelDay && (
              <Text className="fr-sr-only">{ariaLabelDay}</Text>
            )}
            {!loading && dayCard.showDay && ariaLabelDay && (
              <>
                <Text
                  position="absolute"
                  right="1"
                  top="1"
                  {...dayCard.dayCardDay}
                  ariaLabel={ariaLabelDay}
                >
                  {dayjs(day).format(dayDateFormat)}
                </Text>
              </>
            )}
            {!loading && !isPastDate(day) && (
              <>
                <Box
                  display="flex"
                  justifyContent="center"
                  flexDirection="column"
                  alignItems="center"
                  textAlign="center"
                  flex="1"
                  {...dayCard.dayCardBody}
                >
                  {data && data.dataType && data.dataType === 'DATA_API' ? (
                    <>
                      {showDate && (
                        <FareItemDate
                          {...dayCard.dayCardDate}
                          date={data && data.departureDate && data.departureDate}
                        />
                      )}
                      {showSeparator && <Separator content="" {...dayCard.dayCardSeparator} />}
                      {showJourneyTypeTravelClassBeforePrice && journeyTypeTravelClassRender}
                      {showMiles && <FareItemMiles {...data} {...dayCard.dayCardMiles} />}
                      {showPrice && <FareItemPrice {...data} {...dayCard.dayCardPrice} />}
                      {showLastSeen && <FareItemLastSeen {...data} {...dayCard.dayCardLastSeen} />}
                      {!showJourneyTypeTravelClassBeforePrice && journeyTypeTravelClassRender}
                      {showCallToAction && (
                        <FareItemCallToAction
                          marginTop="auto"
                          {...dayCard.dayCardCallToAction}
                          tabIndex={tabIndexCallToAction}
                          onClick={handleCallToAction}
                          onKeyDown={handleCallToActionKeyDown}
                        />
                      )}
                    </>
                  ) : (
                    !isOutside &&
                    (!isDisregarded || (isDisregarded && disregardedBehavior === 'glass-date')) && (
                      <>
                        {showDate && (
                          <FareItemDate
                            {...dayCard.dayCardDate}
                            date={data && data.departureDate && data.departureDate}
                          />
                        )}
                        <FareItemNoPriceAvailable
                          {...dayCard.dayCardNoPriceAvailable}
                          tabIndex={accessibleByArrowKeys ? false : tabIndexCallToAction}
                          onClick={handleCallToAction}
                          onKeyDown={handleCallToActionKeyDown}
                          addAriaLabel={false}
                        />
                      </>
                    )
                  )}
                </Box>
                <Box {...dayCard.dayCardFooter} textAlign="center">
                  {data && data.dataType && data.dataType === 'DATA_API' ? (
                    <>
                      {showReturnInfo && (
                        <FareItemReturnInfo
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          {...data}
                          {...dayCard.dayCardReturnInfo}
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {!isDisregarded ||
                        (isDisregarded &&
                          disregardedBehavior === 'glass-date' &&
                          showNoPriceAvailableCallToAction && (
                            <Button
                              color="link"
                              ariaLabelInFocus
                              {...dayCard.dayCardNoPriceAvailableCallToAction}
                              dataReactCmpOwner="checkAvailabilityCallToAction"
                              onClick={handleCallToAction}
                              onKeyDown={handleCallToActionKeyDown}
                              tabIndex={tabIndexCallToAction ? null : -1}
                              ariaLabel={translate(
                                'cmp-day-card-no-price-link',
                                'Check Availability'
                              )}
                            >
                              {translate('cmp-day-card-no-price-link', 'Check Availability')}
                            </Button>
                          ))}
                    </>
                  )}
                </Box>
              </>
            )}
            {isLowest && data && data.dataType && data.dataType === 'DATA_API' && (
              <Box textAlign="center" style={{ marginBottom: '20px' }} {...dayCard.lowestRowStyle}>
                <Text className="fr-lowest-price-info" {...dayCard.lowestLabelStyle}>
                  {translate('cmp-fare-finder-calendar-lowest', 'Lowest fare')}
                </Text>
              </Box>
            )}
          </>
        )
      )}
    </Box>
  );

  return usedErrorBoundary ? <ErrorBoundary>{cardRender}</ErrorBoundary> : { cardRender };
};

DayCard.propTypes = {
  /**
   * Raw data passed to the object
   */
  data: PropTypes.objectOf(PropTypes.any),
  /**
   * The day to render
   */
  day: PropTypes.objectOf(PropTypes.any),
  /**
   * Determine if the day has the lowest price
   */
  isLowest: PropTypes.bool,
  /**
   * Determine if the day is today
   */
  isToday: PropTypes.bool,
  /**
   * Determine if the day is outside.
   */
  isOutside: PropTypes.bool,
  /**
   * Props of dayCardBody
   */
  dayCardBody: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardFooter
   */
  dayCardFooter: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardReturnInfo
   */
  dayCardReturnInfo: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardTravelClass
   */
  dayCardTravelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardJourneyType
   */
  dayCardJourneyType: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardJourneyTypeTravelClass
   */
  dayCardJourneyTypeTravelClass: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardLastSeen
   */
  dayCardLastSeen: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardPrice
   */
  dayCardPrice: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardMiles
   */
  dayCardMiles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardDay
   */
  dayCardDay: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardNoPriceAvailable
   */
  dayCardNoPriceAvailable: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardNoPriceAvailable
   */
  dayCardDate: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardNoPriceAvailableCallToAction
   */
  dayCardNoPriceAvailableCallToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardCallToAction
   */
  dayCardCallToAction: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of dayCardSeparatorJourneyType
   */
  dayCardSeparatorJourneyType: PropTypes.objectOf(PropTypes.any),
  /**
   * Format applied to the dayCardDay
   */
  dayDateFormat: PropTypes.string,
  /**
   * Turn off or on the showReturnInfo section
   */
  showReturnInfo: PropTypes.bool,
  /**
   * Turn off or on the showTravelClass section
   */
  showTravelClass: PropTypes.bool,
  /**
   * Turn off or on the showJourneyType section
   */
  showJourneyType: PropTypes.bool,
  /**
   * Turn off or on the showLastSeen section
   */
  showLastSeen: PropTypes.bool,
  /**
   * Turn off or on the showPrice section
   */
  showPrice: PropTypes.bool,
  /**
   * Turn off or on the showSeparator section
   */
  showSeparator: PropTypes.bool,
  /**
   * Turn off or on the showMiles section
   */
  showMiles: PropTypes.bool,
  /**
   * Turn off or on the showDay section
   */
  showDay: PropTypes.bool,
  /**
   * Turn off or on the showNoPriceAvailableCallToAction section
   */
  showNoPriceAvailableCallToAction: PropTypes.bool,
  /**
   * Turn off or on the showCallToAction section
   */
  showCallToAction: PropTypes.bool,
  /**
   * Define if conponent is loading
   */
  loading: PropTypes.bool,
  /**
   * Props of currentDayStyles
   */
  currentDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of pastDayStyles
   */
  pastDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of outsideDayStyles
   */
  outsideDayStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of lowestPriceStyles
   */
  lowestPriceStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of noPriceAvailableStyles
   */
  noPriceAvailableStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of currentDayAndLowestPriceStyles
   */
  currentDayAndLowestPriceStyles: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  interactionHotZone: PropTypes.oneOf(['smart', 'whole', 'button']),
  /**
   * OnClick event
   */
  onClick: PropTypes.func,
  /**
   * Call to action onClick event
   */
  callToActionOnClick: PropTypes.func,
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Lowest Price key message for Screen Readers
   */
  lowestPriceMessage: PropTypes.string,
  /**
   * Define if the dayCard will have focus for Screen Readers
   */
  tabIndex: PropTypes.bool,
  /**
   * Function to handle focus renference of the component
   */
  focusReferences: PropTypes.func,
  /**
   * Define if the CallToAction will have focus for Screen Readers
   */
  tabIndexCallToAction: PropTypes.bool,
  /**
   * Define if the CallToAction will have focus for Screen Readers
   */
  showDate: PropTypes.bool,
  /**
   * Cursor of the dayCard
   */
  cursor: PropTypes.oneOf(['default', 'pointer']),
  /**
   * Define if card have scale effect
   */
  effectScale: PropTypes.bool,
  ariaLabel: PropTypes.string,
  ariaLabelFormat: PropTypes.string,
  disregardedBehavior: PropTypes.oneOf(['blank-date', 'past-date', 'glass-date']),
  isDisregarded: PropTypes.bool,
  showJourneyTypeTravelClassBeforePrice: PropTypes.bool
};

DayCard.defaultProps = {
  showSeparator: false,
  data: null,
  showDate: false,
  day: null,
  isLowest: false,
  isToday: false,
  isOutside: false,
  dayCardDate: {},
  dayCardBody: {},
  dayCardFooter: {},
  dayCardReturnInfo: {},
  dayCardJourneyTypeTravelClass: {},
  dayCardTravelClass: {},
  dayCardJourneyType: {},
  dayCardLastSeen: {},
  dayCardPrice: {},
  dayCardMiles: {},
  dayCardDay: {},
  dayCardNoPriceAvailable: {},
  dayCardNoPriceAvailableCallToAction: {},
  dayCardCallToAction: {},
  dayCardSeparatorJourneyType: {},
  dayDateFormat: 'D',
  showReturnInfo: true,
  showTravelClass: true,
  showJourneyType: false,
  showLastSeen: true,
  showPrice: true,
  showMiles: false,
  showDay: true,
  showNoPriceAvailableCallToAction: true,
  showCallToAction: false,
  loading: false,
  usedErrorBoundary: true,
  cursor: 'pointer',
  currentDayStyles: {
    borderColor: 'primary'
  },
  pastDayStyles: {
    disabled: true,
    bgColor: 'light',
    textColor: 'dark-6',
    cursor: 'default',
    dayCardDay: {
      textColor: 'dark-9'
    }
  },
  outsideDayStyles: {
    showDay: false,
    bgColor: 'light',
    cursor: 'default'
  },
  lowestPriceStyles: {
    dayCardDay: {
      textColor: 'lowestFareText'
    },
    dayCardBody: {
      bgColor: 'lowestFareBg',
      textColor: 'lowestFareText'
    }
  },
  noPriceAvailableStyles: {},
  currentDayAndLowestPriceStyles: {},
  lowestPriceMessage: 'cmp-day-card-lowest-price',
  tabIndex: true,
  focusReferences: () => {},
  tabIndexCallToAction: true,
  onClick: () => {},
  callToActionOnClick: () => {},
  interactionHotZone: 'smart',
  effectScale: false,
  ariaLabel: null,
  ariaLabelFormat: 'MMMM D',
  disregardedBehavior: 'past-date',
  isDisregarded: false,
  showJourneyTypeTravelClassBeforePrice: false
};

export default DayCard;
